import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PiArrowLeftDuotone, PiUserPlusDuotone, PiXDuotone } from 'react-icons/pi';
import { getFriends } from '../api/friendService';
import { createRoom } from '../api/roomService';
import { listAllEventTypes } from '../api/eventTypeService';
import { useAuth } from '../app/providers/AuthContext';
import { publicUserName, safeUsername } from '../shared/utils/userDisplay';
import Card from '../shared/common/Card';
import Button from '../shared/common/Button';
import Input from '../shared/common/Input';
import Select from '../shared/common/Select';
import ScoringProfileBuilder, {
  DEFAULT_SCORING_PROFILE,
  toScoringProfilePayload,
  validateScoringRules,
} from '../shared/business/scoring/ScoringProfileBuilder';
import { outcomeSchemeLabel, outcomeSchemeIcon } from '../shared/business/eventType/outcomeSchemeHelpers';

const FRIENDS_LOAD_ERROR = 'Не удалось загрузить список друзей';
const NAME_REQUIRED_ERROR = 'Название обязательно';
  const EVENT_TYPE_REQUIRED_ERROR = 'Тип событий обязателен для создания комнаты';
const BASE_STAKE_REQUIRED_ERROR = 'Базовый множитель должен быть положительным числом';
const MAX_MULTIPLIER_REQUIRED_ERROR = 'Максимальный множитель должен быть положительным числом';
const ROOM_CREATE_ERROR = 'Не удалось создать комнату';
const SCORING_PROFILE_ERROR_KEY = 'scoringProfile';
const FRIENDS_EMPTY_MESSAGE = 'Пока нет друзей. Добавь их на странице «Друзья», чтобы выбирать здесь.';
const BET_MODE_POINTS = 'POINTS';
const DEFAULT_BASE_STAKE = '10';
const DEFAULT_MAX_MULTIPLIER = '3';

export default function CreateRoomPage() {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [baseStake, setBaseStake] = useState(DEFAULT_BASE_STAKE);
  const [maxMultiplier, setMaxMultiplier] = useState(DEFAULT_MAX_MULTIPLIER);
  const [scoringProfile, setScoringProfile] = useState(DEFAULT_SCORING_PROFILE);
  const [friends, setFriends] = useState([]);
  const [selectedFriendIds, setSelectedFriendIds] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [friendsLoading, setFriendsLoading] = useState(false);
  const [friendsError, setFriendsError] = useState('');
  const [isFriendPickerOpen, setIsFriendPickerOpen] = useState(false);
  const [eventTypeId, setEventTypeId] = useState('');
  const [eventTypes, setEventTypes] = useState([]);
  const [eventTypesLoading, setEventTypesLoading] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  const selectedFriends = useMemo(
    () => friends.filter((friend) => selectedFriendIds.includes(friend.id)),
    [friends, selectedFriendIds]
  );

  useEffect(() => {
    const loadFriends = async () => {
      setFriendsLoading(true);
      setFriendsError('');
      try {
        setFriends(await getFriends());
      } catch {
        setFriendsError(FRIENDS_LOAD_ERROR);
      } finally {
        setFriendsLoading(false);
      }
    };

    loadFriends();
  }, []);

  useEffect(() => {
    const loadEventTypes = async () => {
      setEventTypesLoading(true);
      try {
        setEventTypes(await listAllEventTypes());
      } catch {
        // Некритично — типы событий не обязательны
      } finally {
        setEventTypesLoading(false);
      }
    };

    loadEventTypes();
  }, []);

  const validate = () => {
    const errs = {};
    if (!name.trim()) errs.name = NAME_REQUIRED_ERROR;
    if (!eventTypeId) errs.eventTypeId = EVENT_TYPE_REQUIRED_ERROR;
    if (!isPositiveInteger(baseStake)) errs.baseStake = BASE_STAKE_REQUIRED_ERROR;
    if (!isPositiveInteger(maxMultiplier)) errs.maxMultiplier = MAX_MULTIPLIER_REQUIRED_ERROR;
    const scoringProfileError = validateScoringRules(scoringProfile.rules);
    if (scoringProfileError) errs[SCORING_PROFILE_ERROR_KEY] = scoringProfileError;
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setLoading(true);
    setErrors({});
    try {
      await createRoom({
        name: name.trim(),
        description: description.trim(),
        ownerId: user?.id,
        invitedFriendIds: selectedFriendIds,
        betMode: BET_MODE_POINTS,
        baseStake: Number(baseStake),
        maxMultiplier: Number(maxMultiplier),
        scoringProfile: toScoringProfilePayload(scoringProfile),
        eventTypeId: Number(eventTypeId),
      });
      navigate('/rooms');
    } catch {
      setErrors({ global: ROOM_CREATE_ERROR });
    } finally {
      setLoading(false);
    }
  };

  const toggleFriend = (friendId) => {
    setSelectedFriendIds((current) => (
      current.includes(friendId)
        ? current.filter((id) => id !== friendId)
        : [...current, friendId]
    ));
  };

  const removeFriend = (friendId) => {
    setSelectedFriendIds((current) => current.filter((id) => id !== friendId));
  };

  return (
    <div className="bm-animate-in" style={{ maxWidth: 640 }}>
      <Button
        variant="ghost"
        size="sm"
        icon={<PiArrowLeftDuotone size={16} />}
        onClick={() => navigate('/rooms')}
        style={{ marginBottom: 'var(--bm-space-lg)' }}
      >
        Назад к комнатам
      </Button>

      <h1 className="bm-h2" style={{ marginBottom: 'var(--bm-space-xl)' }}>
        Создать комнату
      </h1>

      <Card>
        {errors.global && (
          <div className="bm-auth-error" style={{ marginBottom: 'var(--bm-space-md)' }}>
            {errors.global}
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--bm-space-md)' }}>
          <Input
            label="Название *"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Например: Друзья и футбол 2025"
            error={errors.name}
          />
          <Input
            label="Описание"
            as="textarea"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Краткое описание комнаты (необязательно)"
            rows={3}
          />

          <EventTypeSelect
            eventTypes={eventTypes}
            loading={eventTypesLoading}
            value={eventTypeId}
            onChange={setEventTypeId}
            error={errors.eventTypeId}
          />

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 'var(--bm-space-md)' }}>
            <Input
              label="Базовый множитель очков *"
              type="number"
              value={baseStake}
              onChange={(e) => setBaseStake(e.target.value)}
              placeholder="10"
              min="1"
              error={errors.baseStake}
            />
            <Input
              label="Максимальный множитель *"
              type="number"
              value={maxMultiplier}
              onChange={(e) => setMaxMultiplier(e.target.value)}
              placeholder="3"
              min="1"
              error={errors.maxMultiplier}
            />
          </div>
          <p className="bm-muted" style={{ marginTop: 'calc(var(--bm-space-sm) * -1)' }}>
            Базовый множитель умножается на коэффициент уверенности и очки сработавшего scoring-правила — итог: базовый множитель × уверенность × очки правила. Участник выбирает исход и коэффициент уверенности, рубли не используются.
          </p>

          <ScoringProfileBuilder
            value={scoringProfile}
            onChange={setScoringProfile}
            title="Профиль комнаты по умолчанию"
description="Эти правила применятся к новым пари комнаты, если пари не задаст свои настройки."
            showChangeWarning
            error={errors[SCORING_PROFILE_ERROR_KEY]}
          />

          <div className="bm-friend-picker">
            <div className="bm-friend-picker__summary">
              <div className="bm-friend-picker__copy">
                <span className="bm-label">Пригласить друзей</span>
                <span className="bm-muted">
                  {selectedFriendIds.length
                    ? `Выбрано друзей: ${selectedFriendIds.length}`
                    : 'Можно создать комнату без приглашений и добавить друзей позже.'}
                </span>
              </div>
              <Button
                variant="secondary"
                type="button"
                icon={<PiUserPlusDuotone size={16} />}
                onClick={() => setIsFriendPickerOpen((current) => !current)}
              >
                {isFriendPickerOpen ? 'Скрыть список' : 'Добавить друзей'}
              </Button>
            </div>

            {selectedFriends.length > 0 && (
              <div className="bm-friend-picker__selected" aria-label="Выбранные друзья">
                {selectedFriends.map((friend) => (
                  <button
                    key={friend.id}
                    type="button"
                    className="bm-friend-chip"
                    onClick={() => removeFriend(friend.id)}
                    aria-label={`Убрать ${publicUserName(friend)} из приглашений`}
                  >
                    <span>{publicUserName(friend)}</span>
                    <PiXDuotone size={14} aria-hidden="true" />
                  </button>
                ))}
              </div>
            )}

            {isFriendPickerOpen && (
              <div className="bm-friend-picker__panel">
                {friendsLoading ? (
                  <p className="bm-muted">Загружаем друзей…</p>
                ) : friendsError ? (
                  <div className="bm-auth-error">{friendsError}</div>
                ) : friends.length ? (
                  <div className="bm-friend-picker__list">
                    {friends.map((friend) => (
                      <FriendOption
                        key={friend.id}
                        friend={friend}
                        checked={selectedFriendIds.includes(friend.id)}
                        onToggle={() => toggleFriend(friend.id)}
                      />
                    ))}
                  </div>
                ) : (
                  <p className="bm-muted">{FRIENDS_EMPTY_MESSAGE}</p>
                )}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: 'var(--bm-space-sm)', justifyContent: 'flex-end', paddingTop: 'var(--bm-space-sm)' }}>
            <Button variant="ghost" type="button" onClick={() => navigate('/rooms')}>
              Отмена
            </Button>
            <Button variant="primary" type="submit" loading={loading}>
              Создать комнату
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}

function FriendOption({ friend, checked, onToggle }) {
  return (
    <label className={`bm-check-card${checked ? ' bm-check-card--checked' : ''}`}>
      <input
        className="bm-check-card__input"
        type="checkbox"
        checked={checked}
        onChange={onToggle}
      />
      <span className="bm-check-card__box" aria-hidden="true" />
      <span className="bm-check-card__content">
        <span className="bm-check-card__name">{publicUserName(friend)}</span>
        {safeUsername(friend) && <span className="bm-check-card__hint">@{safeUsername(friend)}</span>}
      </span>
    </label>
  );
}

function isPositiveInteger(value) {
  const number = Number(value);
  return Number.isInteger(number) && number > 0;
}

function EventTypeSelect({ eventTypes, loading, value, onChange, error }) {
  const options = useMemo(() => {
    const items = Array.isArray(eventTypes) ? eventTypes : [];
    return [
      { value: '', label: 'Выберите тип событий…' },
      ...items.map((et) => ({
        value: String(et.id),
        label: `${outcomeSchemeIcon(et.outcomeScheme)} ${et.name} — ${outcomeSchemeLabel(et.outcomeScheme)}`,
      })),
    ];
  }, [eventTypes]);

  return (
    <>
      <Select
        label="Тип событий комнаты"
        value={value}
        onChange={onChange}
        options={options}
        disabled={loading}
        error={error}
        required
      />
      {!error && (
        <p className="bm-muted" style={{ marginTop: 'calc(var(--bm-space-sm) * -1)' }}>
Обязательное поле — в комнате можно создавать только пари выбранного типа
        </p>
      )}
    </>
  );
}
